import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function AdminResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const field =
    "bg-[#0A0C10] border-white/15 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#FF3B30] rounded-none h-12";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw.password !== pw.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw.password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-[#0A0C10] flex items-center justify-center px-4 grain relative">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm"
      >
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-10">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF3B30]" />
          </span>
          <span className="font-heading font-extrabold text-2xl tracking-tight uppercase text-slate-100">
            Ekal <span className="text-[#FF3B30]">Sports</span> Media
          </span>
        </Link>

        <div className="border border-white/10 bg-[#12151C] p-8 space-y-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-400">Restricted — Press Desk</p>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-3xl text-slate-50">
            New Password
          </h1>
          {!token ? (
            <p data-testid="reset-invalid" className="text-xs font-mono text-[#FF3B30]">
              This reset link is missing its token. Request a fresh one from the login page.
            </p>
          ) : done ? (
            <div className="space-y-4">
              <p data-testid="reset-success" className="text-xs font-mono text-emerald-400">
                Password updated. You can log in with it now.
              </p>
              <Link
                to="/admin/login"
                data-testid="reset-login-link"
                className="block text-center bg-[#FF3B30] hover:bg-red-600 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-4 transition-colors"
              >
                Go to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form" className="space-y-4">
              <Input
                data-testid="reset-password-input"
                type="password"
                required
                placeholder="New password (min 8 characters)"
                value={pw.password}
                onChange={(e) => setPw({ ...pw, password: e.target.value })}
                className={field}
              />
              <Input
                data-testid="reset-confirm-input"
                type="password"
                required
                placeholder="Confirm new password"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                className={field}
              />
              {error && (
                <p data-testid="reset-error" className="text-xs font-mono text-[#FF3B30]">
                  {error}
                </p>
              )}
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-4 transition-colors"
              >
                {loading ? "Updating…" : "Set New Password"}
              </button>
            </form>
          )}
          <Link
            to="/admin/login"
            data-testid="reset-back-link"
            className="block text-center text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300 transition-colors"
          >
            ← Back to login
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
